import React, { useState, useEffect, useRef, useMemo } from "react";
import { callChatAPI } from "../services/chatService";
import { projectData, aboutMorpheus } from "../data/portfolioData";
import { FiSend } from "react-icons/fi";

const AiAssistantPanel = ({ onClose }) => {
    const [userInput, setUserInput] = useState("");
    const [chatHistory, setChatHistory] = useState([
        { role: "ai", text: "Hi! I'm Morpheus's AI assistant. Ask me anything about his projects, skills, or experience!" }
    ]);
    const [isChatting, setIsChatting] = useState(false);
    const chatContainerRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        const el = chatContainerRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
        });
    }, [chatHistory, isChatting]);

    const systemPrompt = useMemo(() => `
You are MorpheusAI, a helpful and professional AI assistant for the portfolio website of Morpheus Vibaen J.
Answer ONLY using the information provided in the context below. Do not invent anything.
If something isn't in the context, say: "I don't have that detail here—please ask Morpheus directly."

Formatting rules (IMPORTANT):
- Use short paragraphs and always add line breaks.
- Use this structure whenever possible:

Title: <1 line>
Summary: <1–2 lines>

Key Points:
- <bullet>
- <bullet>
- <bullet>

Relevant Projects (if applicable):
- <Project Title> — <1 line why it's relevant>

Keep it under 200 words unless the user asks for more.

--- MORPHEUS'S INFORMATION ---
ABOUT: ${aboutMorpheus}
PROJECTS: ${JSON.stringify(
        Object.values(projectData).map((p) => ({
            title: p.title,
            description: p.shortDescription,
            tech: p.techStack,
            goal: p.goal,
        }))
    )}
--- END OF INFORMATION ---
`, []);

    const submitMessage = async (text) => {
        if (!text.trim() || isChatting) return;

        const trimmed = text.trim();
        setChatHistory((prev) => [...prev, { role: "user", text: trimmed }]);
        setUserInput("");
        setIsChatting(true);

        try {
            const aiResponseText = await callChatAPI(trimmed, systemPrompt);
            setChatHistory((prev) => [
                ...prev,
                { role: "ai", text: aiResponseText || "Sorry—no response received." },
            ]);
        } catch (err) {
            setChatHistory((prev) => [
                ...prev,
                { role: "ai", text: "Sorry—something went wrong while generating the response." },
            ]);
        } finally {
            setIsChatting(false);
        }
    };

    const handleChatSubmit = (e) => {
        e.preventDefault();
        submitMessage(userInput);
    };

    const handleChipClick = (chip) => {
        if (chip === "Contact") {
            onClose();
            const el = document.getElementById("contact");
            if (el) el.scrollIntoView({ behavior: "smooth" });
            return;
        }

        submitMessage(`Tell me about your ${chip.toLowerCase()}`);
    };

    return (
        <div className="ai-panel">
            <div className="ai-header">
                <h4>✨ Ask AI Assistant</h4>
                <button className="ai-close-btn" onClick={onClose} aria-label="Close assistant">×</button>
            </div>

            <div className="ai-messages" ref={chatContainerRef} data-lenis-prevent aria-live="polite" aria-label="Chat messages">
                {chatHistory.map((msg, index) => (
                    <div key={index} className={`chat-message ${msg.role}`} style={{ whiteSpace: 'pre-wrap' }}>
                        {msg.text}
                    </div>
                ))}
                {isChatting && (
                    <div className="chat-message ai typing-indicator">
                        <span></span><span></span><span></span>
                    </div>
                )}
            </div>

            <div className="ai-quick-chips">
                {["Projects", "Skills", "Contact"].map((chip) => (
                    <button
                        key={chip}
                        className="ai-chip"
                        onClick={() => handleChipClick(chip)}
                    >
                        {chip}
                    </button>
                ))}
            </div>

            <form onSubmit={handleChatSubmit} className="ai-input-row">
                <input
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Ask me anything..."
                    disabled={isChatting}
                    autoFocus
                    aria-label="Ask AI a question"
                />
                <button type="submit" disabled={isChatting || !userInput.trim()} className="ai-send-btn" aria-label="Send message">
                    <FiSend />
                </button>
            </form>
        </div>
    );
};

export default AiAssistantPanel;
